import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../@types/redux';
import { useLocation } from 'react-router';
import AuthThunks from '../../store/auth/thunk';
import GeneralActions from '../../store/general/actions';
import { CustomTooltip, IBaseRight, IOptionType, NavBar, NavDrawer, SideMenuItem, lowercase } from '@zz2/zz2-ui';
import { VERSION } from '../../version';
import UserSettings from './UserSettings';
import Routes from './Routes';
import { ListItemButton, ListItemIcon, ListItemText, Skeleton } from '@mui/material';
import { Link } from 'react-router-dom';
import lodash from 'lodash';
import EnvHelper from '../../service/helper/envHelper';
import FreshServiceTicketCreationDialog from '../freshService/FreshServiceTicketCreationDialog';
import FreshServiceActions from '../../store/freshService/actions';
import { useGetRights } from '../../hooks/query/rights/rightsQueries';
import { RIGHT_GRANTS } from '../../appConstants';
import UserHelper from '../../service/helper/userHelper';
import { IUser } from '../../@types/model/right/user/userModel';
import { getUserSelectedNurseriesLocalStorage, setUserSelectedNurseriesLocalStorage } from '../../service/localStorage/localStorageService';
import OptionType from '../../@types/model/optionType';
import { INursery } from '../../@types/model/masterData/nursery/nursery';

const iconsLocation = `${ASSET_BASE}/assets/icons`;

const NavFrame = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const path = useLocation().pathname;

    const currentUser = useAppSelector<IUser | null>(x => x.auth.session?.user ?? null);
    const isNavDrawerOpen = useAppSelector(x => x.general.isNavDrawerOpen);
    const freshTicketServiceDescription = useAppSelector(x => x.freshService.supportTicketDescription);
    const isFreshServiceTicketDialogOpen = useAppSelector(x => x.freshService.isSupportTicketDialogOpen);

    const userSelectedNurseries : Array<IOptionType> = getUserSelectedNurseriesLocalStorage();

    const [isUserSettingsDialogOpen, setIsUserSettingsDialogOpen] = useState<boolean>(false);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    const { isFetching: isLoadingRights, data: rights, refetch: loadRights } = useGetRights(false);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {    
        loadData();
    }, [userSelectedNurseries]);

    useEffect(() => {
        if (isNavDrawerOpen) {
            loadRights();
        }
    }, [isNavDrawerOpen]);

    useEffect(() => {
        if (!currentUser?.userNurseries) return;

        if (currentUser.userNurseries.length > 0) {
            const localNurseryIds = userSelectedNurseries.map(x => Number(x.value));

            const selectedNurseries : Array<IOptionType> = currentUser.userNurseries
                .filter(x =>
                    x.isActive
                    && !!x.nursery
                    && !!x.nursery.isActive
                    && localNurseryIds.includes(x.nurseryId))
                .map((x) => OptionType.fromDataModel(x.nursery as INursery));

            setUserSelectedNurseriesLocalStorage(selectedNurseries);
        }
    }, [currentUser, currentUser?.userNurseries]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const loadData = () : void => {
        if (currentUser) {
            if (userSelectedNurseries.length < 1) {
                setIsUserSettingsDialogOpen(true);
            }
        }
    };

    const signOut = () : void => {
        dispatch(GeneralActions.setNavDrawer(false));
        dispatch(AuthThunks.logout());
    };

    const openUserSettings = () : void => {
        setIsUserSettingsDialogOpen(true);
    };

    const closeUserSettings = () : void => {
        setIsUserSettingsDialogOpen(false);
    };

    const openDrawer = () : void => {
        dispatch(GeneralActions.setNavDrawer(true));
    };

    const closeDrawer = () : void => {
        dispatch(GeneralActions.setNavDrawer(false));
    };

    const handleFreshServiceTicketDialogOpen = () : void => {
        dispatch(FreshServiceActions.setIsSupportTicketDialogOpen(true));
    };

    const handleFreshServiceTicketDialogClose = () : void => {
        dispatch(FreshServiceActions.setIsSupportTicketDialogOpen(false));
    };

    const requestAccess = (right : IBaseRight) : void => {
        dispatch(FreshServiceActions.setSupportTicketDescription(`Requesting permission: ${right.name} - View.`));
        dispatch(FreshServiceActions.setIsSupportTicketDialogOpen(true));
    };

    const isParentOfCurrentPage = (parentId : number, rights ?: Array<IBaseRight>, pathname ?: string) : boolean => {
        const currentPageRight = rights?.find(x => x.url === pathname);
        const parentRight = rights?.find(x => x.id === parentId);

        if (currentPageRight?.parentId) {
            return (currentPageRight.parentId === parentId) || (!!parentRight?.parentId && isParentOfCurrentPage(parentRight.parentId, rights, pathname));
        }
        return false;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const nurseryHeading = useMemo<string>(() => {
        if (!currentUser) return '';
        return UserHelper.getUserNurseryHeading(currentUser);
    }, [currentUser, userSelectedNurseries, isUserSettingsDialogOpen]);

    const userSettings = useMemo<React.ReactElement>(() => {
        if (!isUserSettingsDialogOpen) return <div/>;
        
        return <UserSettings 
            isOpen={isUserSettingsDialogOpen}
            currentUser={currentUser ?? null}
            onClose={closeUserSettings}
            onLogout={signOut}
        />;
    }, [isUserSettingsDialogOpen, currentUser]);

    const currentUserRights = useMemo(() => {
        return currentUser?.userRights.map(x => x.right);
    }, []);

    const userCurrentRight = useMemo<IBaseRight | undefined>(() => {
        const urlSections = path.split('/'); 
        const firstPartition = urlSections.slice(0, -1).join('/');
        
        return currentUserRights?.filter(x => x.isActive)?.find(
            x => x.url === (urlSections[urlSections.length - 1].includes('Id') 
                ? firstPartition
                : path
            )
        );

    }, [currentUser, path]);


    const breadcrumbs = useMemo<Array<{ name : string; url : string }>>(() => {
        const breadcrumbList : Array<{ name : string; url : string }> = [];

        if (userCurrentRight) {
            let currentRight : IBaseRight | undefined = userCurrentRight;

            do {
                breadcrumbList.push({ name: currentRight.name, url: currentRight.url ?? '' });
                currentRight = currentUserRights?.find(x => x.id === currentRight?.breadcrumbParentId);
            } while (currentRight);
        }
        return breadcrumbList.reverse();
    }, [currentUser, location, currentUserRights, userCurrentRight]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    const renderListSection = (right : IBaseRight, children : Array<IBaseRight>, rights ?: Array<IBaseRight>, pathname ?: string) : JSX.Element => {
        const userRights = currentUser?.userRights;
        const hasViewRight = userRights?.some(x =>
            x.isActive
            && x.right.id === right.id
            && x.rightGrantLevel === RIGHT_GRANTS.View);

        return (
            <CustomTooltip
                title={!hasViewRight ? `Right: ${right.name}, level: View is required for access. \n\nClick to request permission.` : ''}
                onClick={() : void => requestAccess(right)}
                rightValidation={!hasViewRight}
                key={'section_custom_tooltip_' + right.id}>
                <SideMenuItem
                    className={'pt10'}
                    paddingLeft={0}
                    icon={<img className={'h30 w30'} src={`${iconsLocation}/${lowercase(right.name).replace(/ /g, '_')}.svg`}/> }
                    title={right.name}
                    key={'section_' + right.id}
                    boldTitle={isParentOfCurrentPage(right.id, rights, pathname)}
                    disabled={!hasViewRight}>
                    {children.map(x => renderListItem(x, false, pathname))}
                </SideMenuItem>
            </CustomTooltip>
        );
    };

    const renderListItem = (right : IBaseRight, hasIcon : boolean, pathname ?: string) : JSX.Element => {
        const userRights = currentUser?.userRights;
        const hasViewRight = userRights?.some(x =>
            x.isActive
            && x.right.id === right.id
            && x.rightGrantLevel === RIGHT_GRANTS.View);

        return (
            <CustomTooltip
                title={!hasViewRight ? `Right: ${right.name}, level: View is required for access. \n\nClick to request permission.` : ''}
                onClick={() : void => requestAccess(right)}
                rightValidation={!hasViewRight}
                key={'list_item_custom_tooltip_' + right.id}>
                <Link
                    key={`link ${right.url}`}
                    to={right.url ?? ''}
                    onClick={closeDrawer}
                    style={{ pointerEvents: hasViewRight ? 'auto' : 'none', textDecoration: 'unset', color: 'unset' }}
                    className={'tdus cb'}
                >
                    <ListItemButton 
                        key={right.guid} 
                        disabled={!hasViewRight}>
                        {
                            hasIcon ?
                                <ListItemIcon>
                                    {<img className={'h30 w30 mt8 mb8'} src={`${iconsLocation}/${lowercase(right.name).replace(/ /g, '_')}.svg`} />}
                                </ListItemIcon>
                                :
                                <div className={'h30 w30'} />
                        }
                        <ListItemText disableTypography className={`${pathname === right.url ? 'fw550' : ''} pl0`} inset primary={right.name} />
                    </ListItemButton>
                </Link>
            </CustomTooltip>
        );
    };

    const sections = useMemo<Array<JSX.Element>>(() => {
        if (isLoadingRights) {
            return [...Array(5)].map((_, index) => (
                <Skeleton 
                    key={`skeleton-${index}`} 
                    animation='wave' 
                    variant='rectangular' 
                    width={295} 
                    height={60} 
                    className={'mb10 br10'} 
                />
            ));
        }

        if (!rights) return [];     

        return lodash.chain(rights)
            .filter(x => x.isOnNavDrawer && x.isActive && !x.parentId)
            .filter(x => !!x.sectionOrder)
            .sortBy(x => x.sectionOrder)
            .map((x) => {
                const userRights = currentUser?.userRights;
                const hasParentViewRight = userRights?.some(userRight =>
                    userRight.isActive
                    && userRight.right.id === x.id
                    && userRight.rightGrantLevel === RIGHT_GRANTS.View) ?? false;

                return {
                    hasParentViewRight,
                    section: x,
                    children: lodash.chain(rights)
                        .filter(y => y.isOnNavDrawer && y.isActive && y.parentId === x.id)
                        .filter(y => !!y.pageOrder)
                        .sortBy(y => y.pageOrder)
                        .map((child) => {
                            const hasChildViewRight = userRights?.some(userRight =>
                                userRight.isActive
                                && userRight.right.id === x.id
                                && userRight.rightGrantLevel === RIGHT_GRANTS.View) ?? false;

                            return {
                                hasChildViewRight,
                                child,
                            };
                        })
                        .orderBy(x => x.hasChildViewRight, 'desc')
                        .value(),
                };
            })
            .orderBy(x => x.hasParentViewRight, 'desc')
            .map((x) => {
                return x.section.isPage
                    ? renderListItem(x.section, true, path)
                    : renderListSection(x.section, x.children.map(x => x.child), currentUserRights, path);
            }).value();

    }, [currentUser, rights, path, isLoadingRights]);

    return (
        <div className={'fdc hfill'}>
            <NavBar
                env={EnvHelper.getEnvName()}
                version={VERSION.version}
                currentUser={currentUser}
                signOut={signOut}
                breadcrumbs={breadcrumbs}
                openDrawer={openDrawer}
                closeDrawer={closeDrawer}
                openUserSettings={openUserSettings}
                closeUserSettings={closeUserSettings}
                headings={[nurseryHeading]}
                disabled={false}       
                userSettingsDialog={userSettings}
                isLatestVersion={true}             
            />
            <div className={'fdr flx1 oyh'}>
                <NavDrawer
                    env={EnvHelper.getEnvName()} 
                    path={path}
                    currentUser={currentUser}
                    isOpen={isNavDrawerOpen}
                    isLoading={false}
                    sections={sections}
                    logOut={signOut}
                    closeDrawer={closeDrawer}
                    onHelpClick={handleFreshServiceTicketDialogOpen}
                />
                <Routes />            
            </div>
            {isFreshServiceTicketDialogOpen &&
                <FreshServiceTicketCreationDialog 
                    isOpen={isFreshServiceTicketDialogOpen}
                    onDismiss={handleFreshServiceTicketDialogClose}
                    description={freshTicketServiceDescription}
                />
            }
        </div>
    );
};

export default NavFrame;